import { useState } from "react";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { faGripVertical, faTrash } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { BaseButton } from "shared/atoms/BaseButton";
import { useLocalizedDate } from "utils/date";
import { translateActivityType } from "utils/translations";

import {
  ActionButton,
  Actions,
  CompletedDate,
  Container,
  Content,
  ExerciseImage,
  Level,
  MetaRow,
  RequiredDot,
  Separator,
  Title,
  TitleRow,
  TypeLabel,
} from "../CalendarCard/CalendarCard";
import { CommentButton } from "../shared";

import { CommentDialog } from "./CommentDialog";
import type { SortableProtocolCard } from "./types";
import { isActivity, isExercise, isLesson } from "./utils";

interface ProtocolTimelineCardProps {
  activity: SortableProtocolCard;
  isDisabled?: boolean;
  isDragging?: boolean;
  smartReply: string | null | undefined;
  smartReplyId: number | null | undefined;
  week: number;
  onPreview?: () => void;
  onRemove?: () => void;
}

export const ProtocolTimelineCard: React.FC<ProtocolTimelineCardProps> = ({
  activity,
  isDisabled,
  isDragging,
  smartReply,
  smartReplyId,
  week,
  onPreview,
  onRemove,
}) => {
  const { t } = useTranslation();
  const { format, parseISO } = useLocalizedDate();
  const [showComment, setShowComment] = useState(false);

  const { attributes, listeners, transform, transition, setNodeRef } = useSortable({
    id: activity.sortableID,
    data: {
      type: "protocol_timeline_card",
      activity,
      activityType: activity.activity_type_trimmed,
      week,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getClassName = () => {
    if (activity.completed_on) {
      return "completed";
    }
    if (isDisabled) {
      return "disabled";
    }
    if (isDragging) {
      return "dragging";
    }

    return "";
  };

  const isDragDisabled = isDisabled || !["exercise", "lesson"].includes(activity.activity_type_trimmed);
  const dragHandleProps = isDragDisabled ? {} : { ...attributes, ...listeners };

  return (
    <>
      <Container
        ref={isDragDisabled ? null : setNodeRef}
        className={getClassName()}
        style={style}
        $schedulerVersion={2}
        {...dragHandleProps}
      >
        {!isDragDisabled && (
          <DragHandle $isLocked={isDisabled || false} title={isDisabled ? "Dragging disabled" : "Drag to reorder"}>
            <FontAwesomeIcon icon={faGripVertical} />
          </DragHandle>
        )}

        {(isExercise(activity) || isLesson(activity)) && (
          <ExerciseImage
            src={activity.image_url || (isExercise(activity) ? activity.video_thumbnail_file_url : null) || ""}
            alt={activity.title}
          />
        )}

        <Content>
          <TitleRow>
            <Title>{activity.title}</Title>
            {isActivity(activity) && activity.required && <RequiredDot title="Required" />}
          </TitleRow>

          <MetaRow>
            <TypeLabel>
              {translateActivityType(activity.activity_type_trimmed as "activity" | "exercise" | "lesson")}
            </TypeLabel>
            {isExercise(activity) && activity.current_level && activity.max_level && (
              <>
                <Separator>•</Separator>
                <Level>{t("activities.level", { current: activity.current_level, total: activity.max_level })}</Level>
              </>
            )}
            {activity?.completed_on && (
              <CompletedDate>
                {t("activities.completed")} {format(parseISO(activity.completed_on), "MMM d")}
              </CompletedDate>
            )}
          </MetaRow>
        </Content>

        {(isExercise(activity) || isLesson(activity)) && (
          <Actions>
            {onRemove && (
              <ActionButton
                onClick={e => {
                  e.stopPropagation();
                  onRemove();
                }}
                $variant="danger"
                title="Remove"
                disabled={isDisabled}
              >
                <FontAwesomeIcon icon={faTrash} />
              </ActionButton>
            )}

            {onPreview && (
              <BaseButton
                variant="action"
                icon="eye"
                size="small"
                title={t("treatment.editor.preview_activity", {
                  activityType: isExercise(activity) ? "exercise" : "lesson",
                })}
                onClick={event => {
                  event.stopPropagation();
                  onPreview();
                }}
              />
            )}

            {activity.comment && (
              <CommentButton hasReply={Boolean(activity.reply)} onClick={() => setShowComment(true)} />
            )}
          </Actions>
        )}
      </Container>

      {showComment && activity.comment && activity.patient_id && activity.user_activity_id && (
        <CommentDialog
          activity={activity}
          smartReply={smartReply}
          smartReplyId={smartReplyId}
          isOpen={showComment}
          setIsOpen={setShowComment}
          onPreview={onPreview}
        />
      )}
    </>
  );
};

const DragHandle = styled.div<{ $isLocked: boolean }>`
  display: flex;
  align-items: center;
  color: ${({ $isLocked }) => ($isLocked ? "#d0d7de" : "#8391a1")};
  cursor: ${({ $isLocked }) => ($isLocked ? "not-allowed" : "grab")};
  padding: 4px;
  opacity: ${({ $isLocked }) => ($isLocked ? 0.5 : 1)};

  &:active {
    cursor: ${({ $isLocked }) => ($isLocked ? "not-allowed" : "grabbing")};
  }
`;
